import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Dialog } from "@mui/material";
import * as React from "react";

import { AlertDialog, PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import {
  DatasetsList as SharedDatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { useDeleteDatasets } from "@/shared/domain/datasets/hooks";
import { usePopupState } from "@/shared/hooks/usePopupState";

import { CreateDatasetPopoverButton } from "./CreateDatasetPopoverButton";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
interface PendingDatasetAction {
  ids: string[];
  clearSelection: () => void;
}

export function DatasetsList() {
  const { mutateAsync: deleteDatasets } = useDeleteDatasets();
  const showNotification = useNotification();

  const addToCollectionPopup = usePopupState<PendingDatasetAction>();
  const { open: openAddToCollectionPopup } = addToCollectionPopup;
  const pendingAdd = addToCollectionPopup.metadata;

  const [pendingDelete, setPendingDelete] =
    React.useState<PendingDatasetAction | null>(null);

  const selectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            metadata: {
              ids: selected.map((d) => d.dataset_id),
              clearSelection,
            },
          });
        },
      },
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingDelete({
            ids: selected.map((d) => d.dataset_id),
            clearSelection,
          });
        },
      },
    ],
    [openAddToCollectionPopup],
  );

  const handleDeleteConfirm = async () => {
    if (pendingDelete === null) {
      return;
    }
    const results = await deleteDatasets(pendingDelete.ids);
    const failures = results.filter((r) => r.status === "rejected").length;
    if (failures > 0) {
      showNotification(
        `${failures} dataset${failures !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
    pendingDelete.clearSelection();
  };

  return (
    <PageLayout title="Roboto - Datasets" headerContentLeft="Datasets">
      <SharedDatasetsList
        selectionOptions={selectionOptions}
        actions={<CreateDatasetPopoverButton />}
      />
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "Dataset",
            items: new Set(pendingAdd?.ids ?? []),
          }}
          onCancel={addToCollectionPopup.close}
          onSuccess={() => pendingAdd?.clearSelection()}
        />
      </Dialog>
      <AlertDialog
        dialogOpen={pendingDelete !== null}
        handleClose={() => setPendingDelete(null)}
        dialogTitle={`Delete ${pendingDelete?.ids.length ?? 0} dataset${pendingDelete?.ids.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
